import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import moment from 'moment';
import jsonpath from 'jsonpath';
import { configureAxios } from '@parchment/oauth2-axios';
import { Col, Row } from 'react-bootstrap';
import { LinkButton, MemberInfoBanner } from '@parchment/ui-primitives';
import {
    ExpiredCredentialDisplayIcon,
    RevokedCredentialDisplayIcon,
    ValidIcon,
    VerifiedByParchmentLogoIcon
} from '@parchment/icons';
import {
    alto,
    Banner,
    SimplePaginator,
    thunderBird,
    wildSand
} from '@parchment/atoms';
import DocumentPreview from '@parchment/document-preview';
import { getExternalEvidence } from '../actions/ExternalEvidenceActions';
import { getUiConfig } from '../actions/UiConfigActions';
import { EXTERNAL_EVIDENCE_SOURCE_TYPE } from '../constants/ExternalEvidenceSourceType';
import { CREDENTIAL_ACCOUNT_RESOURCE_KEY } from '../utils';
import AchievementShareModal from './AchievementShareModal';
import LearnerInfoBanner from './LearnerInfoBanner';

const StyledCredentialPage = styled.div`
    margin-top: 100px;

    .button-section {
        padding: 30px 60px;
    }

   .credential-page-header a {
        width: 100%;
    }

    @media (min-width: 768px) {
        .credential-page-header a {
            float: right;
            width:auto;
        }
    }

`;
const StyledCredentialSection = styled.div`
    border: 1px solid ${alto};
    margin: 15px 0 30px 0;
`;
const StyledCredentialSectionHeader = styled.div`
    background-color: ${wildSand};
    border-bottom: 1px solid ${alto};
    font-size: 30px;
    min-height: 60px;
    line-height: 60px;
    text-align: center;
    text-transform: uppercase;
`;
const StyledCredentialSectionBanner = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 10px 15px 0 15px;
`;
const StyledCredentialSectionBody = styled.div`
    padding: 10px 15px 15px 15px;
`;
const StyledCredentialSectionBodySmall = styled.div`
    display: none;

    @media (min-width: 768px) {
        display: flex;
        padding: 10px 15px 15px 15px;
    }
`;
const StyledCredentialPreview = styled.div`
    border: 1px solid ${alto};

    img {
        width: 100%;
    }

    .revoked-credential-display-icon,
    .expired-credential-display-icon {
        width: 100%;
        height: 100%;
    }
`;
const StyledCredentialPreviewSmall = styled.div`
    border: 1px solid ${alto};
    flex-basis: 40%;

    img {
        width: 100%;
    }
`;
const StyledCredentialMetadata = styled.div`
    line-height: 20px;

    .metadata-group {
        margin-top: 15px;
        vertical-align: top;
    }
    .metadata-label {
        font-weight: bold;
    }
    .verified-icon {
        font-size: 40px;
        text-align: right;
    }
    #expiredDateSection, .verified-icon {
        display: inline-block;
        width: 50%;
    }

    .expired-text {
        color: ${thunderBird};
    }

    @media (min-width: 768px) {
        .metadata-group, #expiredDateSection {
            display: inline-block;
            width: 20%;
        }
    }
`;
const StyledCredentialMetadataSmall = styled.div`
    margin-left: 20px;

    .metadata-group {
        margin-top: 10px;
    }
    .metadata-label {
        font-weight: bold;
    }
    .verified-icon {
        margin-top: 25px;
        font-size: 40px;
    }
`;
const StyledEvidenceSectionBody = styled.div`
    padding: 0 15px 15px 15px;
`;
const StyledEvidenceHeader = styled.div`
    font-size: 18px;
    font-weight: bold;
    margin-top: 15px;
    margin-bottom: 20px;

    @media (min-width: 768px) {
        font-size: 24px;
        margin-top: 0;
        margin-bottom: 30px;
    }
`;
const StyledEvidenceMetadataHeader = styled.div`
    font-size: 14px;
    font-weight: bold;

    @media (min-width: 768px) {
        font-size: 18px;
    }
`;
const StyledEvidenceMetadata = styled.div`
    font-size: 14px;
    margin-bottom: 15px;

    @media (min-width: 768px) {
        font-size: 16px;
        margin-bottom: 20px;
    }
`;
const CustomStyledEvidenceMetadata = styled.p`
    a {
        overflow-wrap: break-word;
    }
    ${(props) => props.metadata.valueHtmlCss}
`;

const EXTERNAL_EVIDENCE_DATA_TYPE = {
    text: 'text',
    link: 'link',
    list: 'list'
};

export class App extends Component {
    static get propTypes() {
        return {
            award: PropTypes.shape({
                id: PropTypes.number,
                name: PropTypes.string,
                earnerDisplayName: PropTypes.string,
                awardDisplayTitle: PropTypes.string,
                formattedAwardDate: PropTypes.string,
                formattedExpirationDate: PropTypes.string,
                archived: PropTypes.bool,
                orderable: PropTypes.bool,
                user: PropTypes.shape({
                    firstName: PropTypes.string,
                    lastName: PropTypes.string,
                    id: PropTypes.number,
                    email: PropTypes.string
                }),
                credential: PropTypes.shape({
                    name: PropTypes.string,
                    credentialTypeName: PropTypes.string,
                    productName: PropTypes.string,
                    productId: PropTypes.number
                }),
                issuer: PropTypes.shape({
                    id: PropTypes.number,
                    name: PropTypes.string,
                    address: PropTypes.shape({
                        city: PropTypes.string,
                        state: PropTypes.string,
                        country: PropTypes.string
                    })
                }),
                preview: PropTypes.shape({
                    large: PropTypes.string
                }),
                data: PropTypes.string,
                externalEvidenceId: PropTypes.number
            }),
            userProfileImageUrl: PropTypes.string,
            memberLogoUrl: PropTypes.string,
            memberWebsiteUrl: PropTypes.string,
            memberLinkedinUrl: PropTypes.string,
            externalEvidence: PropTypes.object,
            uiConfig: PropTypes.object,
            credentialAccountApiUrl: PropTypes.string,
            indianaAchievementShare: PropTypes.bool,
            learnerBannerUserImageUrl: PropTypes.string,
            dashboardUrl: PropTypes.string,
            isOwner: PropTypes.bool,
            accessToken: PropTypes.func
        };
    }

    constructor(props) {
        super(props);
        const { award, credentialAccountApiUrl, accessToken } = this.props;

        configureAxios({
            name: CREDENTIAL_ACCOUNT_RESOURCE_KEY,
            config: {
                baseURL: credentialAccountApiUrl
            },
            onGetAccessToken: accessToken
        });

        const expired = award.formattedExpirationDate ?
            moment().isAfter(moment(award.formattedExpirationDate, 'MMM DD, YYYY')) : false;
        this.state = {
            expired: expired,
            currentPage: 1,
            displayExternalEvidence: award.externalEvidenceId && !award.archived && !expired,
            externalEvidencePages: null,
            showAchievementShareModal: false
        };
    }

    componentDidMount() {
        const { getExternalEvidence, getUiConfig, award } = this.props;
        const { displayExternalEvidence } = this.state;

        if (displayExternalEvidence) {
            getExternalEvidence(award.externalEvidenceId).then(() => {
                if (this.props.externalEvidence.data && this.props.externalEvidence.data.sourceType) {
                    getUiConfig(EXTERNAL_EVIDENCE_SOURCE_TYPE[this.props.externalEvidence.data.sourceType]).then(() => {
                        this.setState({ externalEvidencePages: this.getExternalEvidenceForDisplay() });
                    });
                }
            });
        }
    }

    /**
     * apply uiConfig to externalEvidence and filter out tabs/fields without a value
     * return externalEvidence pages array for display (it's essentially the uiConfig array with evidence values embedded)
     */
    getExternalEvidenceForDisplay() {
        const { externalEvidence, uiConfig } = this.props;

        let evidenceObj = null;
        if (externalEvidence.data && externalEvidence.data.rawJson) {
            try {
                evidenceObj = JSON.parse(externalEvidence.data.rawJson);
            } catch(e) {
                console.error('Error parsing external evidence json.', e);
                return null;
            }
        }

        let evidenceConfigPages = null;
        if (uiConfig.data && uiConfig.data.configDocument) {
            try {
                const uiConfigDocumentObj = JSON.parse(uiConfig.data.configDocument);

                if (uiConfigDocumentObj && uiConfigDocumentObj.evidenceDisplay
                        && uiConfigDocumentObj.evidenceDisplay.length > 0) {
                    evidenceConfigPages = uiConfigDocumentObj.evidenceDisplay;
                }
            } catch(e) {
                console.error('Error parsing ui config json.', e);
                return null;
            }
        }

        if (evidenceObj && evidenceConfigPages) {
            const evidencePagesForDisplay = [];
            evidenceConfigPages.forEach((evidencePage) => {
                if (evidencePage.content) {
                    const currentPageContent = [];
                    evidencePage.content.forEach((metadata) => {
                        const valueArray = jsonpath.query(evidenceObj, metadata.valueJsonPath);
                        if (valueArray && valueArray.length > 0) {
                            currentPageContent.push(
                                Object.assign({}, metadata, { _valueArray: valueArray })
                            );
                        }
                    });

                    if (currentPageContent.length > 0) {
                        evidencePagesForDisplay.push(
                            Object.assign({}, evidencePage, { content: currentPageContent })
                        );
                    }
                }
            });
            return evidencePagesForDisplay;
        }
        return null;
    }

    handleContextMenu = (event) => {
        event.preventDefault();
    };

    handleEvidencePaginatorChange = (current) => {
        this.setState({'currentPage': current});
    };

    renderEvidencePaginator() {
        const { displayExternalEvidence } = this.state;
        if (!displayExternalEvidence) {
            return null;
        }

        const loadingEvidence = this.props.externalEvidence.loading ||
            this.props.uiConfig.loading;

        let pageCount = 1;
        if (this.state.externalEvidencePages && this.state.externalEvidencePages.length > 0) {
            pageCount += this.state.externalEvidencePages.length;
        }
        const paginatorSize = (window.screen.width < 768 ) ? 'small' : 'medium';

        return (<SimplePaginator name="credential-evidence-paginator"
                                 size={paginatorSize} pageCount={pageCount}
                                 loading={loadingEvidence}
                                 onChange={this.handleEvidencePaginatorChange} />);
    }

    renderCredentialPreviewImage() {
        const { award } = this.props;

        if (award.archived) {
            return (
                <RevokedCredentialDisplayIcon className='revoked-credential-display-icon' />
            );
        } else if (this.state.expired) {
            return (
                <ExpiredCredentialDisplayIcon className='expired-credential-display-icon' />
            );
        } else {
            return (
                <img src={award.preview.large} onContextMenu={this.handleContextMenu}
                     alt={award.credential.name} data-target="download" />
            );
        }
    }

    renderCredentialMetaData() {
        const { award } = this.props;
        if (award.archived) {
            return null;
        }
        return (
            <div>
                <div id='productNameSection' className="metadata-group">
                    <div className="metadata-label">{award.credential.productName}</div>
                    <div id='displayTitle'>{award.awardDisplayTitle}</div>
                </div>
                <div id='learnerNameSection' className="metadata-group">
                    <div className="metadata-label">Issued To</div>
                    <div id='learnerName'>
                        {`${award.user.firstName} ${award.user.lastName}`}
                    </div>
                </div>
                <div id='awardDateSection' className="metadata-group">
                    <div className="metadata-label">Date Acquired:</div>
                    <div id='awardDate'>{award.formattedAwardDate}</div>
                </div>
                { this.renderExpirationDateData() }
                <div className="metadata-group verified-icon">
                    <VerifiedByParchmentLogoIcon />
                </div>
            </div>
        );
    }

    renderExpirationDateData() {
        const { award } = this.props;
        const { expired } = this.state;

        return (
            <div id='expiredDateSection' className={`metadata-group ${expired ? 'expired-text' : ''}`}>
                <div className="metadata-label">{expired ? 'Expired:' : 'Expires:'}</div>
                <div id='expiredDate'>
                    { award.formattedExpirationDate ? award.formattedExpirationDate : 'Does Not Expire' }
                </div>
            </div>
        );
    }

    renderOrderButton() {
        const { award } = this.props;

        if (!award.archived && award.orderable && !this.state.expired) {
            let url = `/u/registration/${award.issuer.id}/account`;
            return (
                <Col lg={6} md={6} sm={6} className="button-section">
                    <LinkButton
                        target="_blank"
                        href={url}
                        className="btn-success"
                        label="Order This Credential" />
                </Col>
            );
        }
        return null;
    }

    renderCredentialSectionBanner() {
        const { award } = this.props;

        let isCredentialValid = false;
        const bannerProps = {
            className: 'credential-status-banner',
            message: ''
        };
        if (award.archived) {
            Object.assign(bannerProps, {
                context: 'error',
                title: 'revoked',
                message: 'This credential was revoked by the issuing authority'
            });
        } else if (this.state.expired) {
            const timeDifference = moment(award.formattedExpirationDate, 'MMM DD, YYYY').fromNow(true);
            Object.assign(bannerProps, {
                context: 'error',
                title: 'expired',
                message: `This credential expired ${timeDifference} ago and is no longer valid`
            });
        } else {
            isCredentialValid = true;
            Object.assign(bannerProps, {
                context: 'ghost',
                title: `Valid ${award.credential.name}`,
                icon: (<ValidIcon contextType='text' />)
            });
        }
        const banner = (<Banner {...bannerProps} />);

        if (isCredentialValid) {
            return (
                <StyledCredentialSectionBanner>
                    { banner }
                    { this.renderEvidencePaginator() }
                </StyledCredentialSectionBanner>
            );
        } else {
            return banner;
        }
    }

    renderCredentialSectionBody() {
        const { currentPage } = this.state;
        const { award } = this.props;
        // 266 is Learner Record
        if (award.credential.productId === 266) {
            return (
                <StyledCredentialSectionBody className="section-body">
                    <DocumentPreview
                        file={award.data}
                        blockRightClick={false}
                        inline />
                    <StyledCredentialMetadata className="credential-metadata">
                        {this.renderCredentialMetaData()}
                    </StyledCredentialMetadata>
                </StyledCredentialSectionBody>
            );

        } else if (currentPage === 1) {
            return (
                <StyledCredentialSectionBody className="section-body">
                    <StyledCredentialPreview className="credential-preview">
                        {this.renderCredentialPreviewImage()}
                    </StyledCredentialPreview>

                    <StyledCredentialMetadata className="credential-metadata">
                        {this.renderCredentialMetaData()}
                    </StyledCredentialMetadata>
                </StyledCredentialSectionBody>
            );
        } else {
            return (
                <StyledCredentialSectionBodySmall className="section-body">
                    <StyledCredentialPreviewSmall className="credential-preview">
                        { this.renderCredentialPreviewImage() }
                    </StyledCredentialPreviewSmall>

                    <StyledCredentialMetadataSmall className="credential-metadata">
                        { this.renderCredentialMetaData() }
                    </StyledCredentialMetadataSmall>
                </StyledCredentialSectionBodySmall>
            );
        }
        return (
            <StyledCredentialSectionBodySmall className="section-body">
                <StyledCredentialPreviewSmall className="credential-preview">
                    { this.renderCredentialPreviewImage() }
                </StyledCredentialPreviewSmall>

                <StyledCredentialMetadataSmall className="credential-metadata">
                    { this.renderCredentialMetaData() }
                </StyledCredentialMetadataSmall>
            </StyledCredentialSectionBodySmall>
        );
    }

    renderExternalEvidenceSectionBody() {
        const { displayExternalEvidence, currentPage, externalEvidencePages } = this.state;
        if (!displayExternalEvidence || currentPage <= 1
            || externalEvidencePages === null) {
            return null;
        }

        // externalEvidence is displayed in the following pages after first page
        const evidenceDataIndex = currentPage - 2;
        const currentEvidencePage = externalEvidencePages[evidenceDataIndex];
        if (currentEvidencePage) {
            let metadataIndex = 0;
            const CustomStyledEvidenceHeader = styled.div`${currentEvidencePage.displayTitleHtmlCss}`;
            return (
                <StyledEvidenceSectionBody className='credential-evidence'>
                    <div>
                        <StyledEvidenceHeader className='credential-evidence-header'>
                            <CustomStyledEvidenceHeader
                                className={currentEvidencePage.displayTitleHtmlClasses}>
                                {currentEvidencePage.displayTitle}
                            </CustomStyledEvidenceHeader>
                        </StyledEvidenceHeader>
                        {
                            currentEvidencePage.content.map((metadata) => {
                                metadataIndex++;
                                const CustomStyledEvidenceMetadataHeader = styled.div`${metadata.displayNameHtmlCss}`;
                                return metadata && (
                                    <div
                                        className='credential-evidence-metadata'
                                        key={`metadata-${metadataIndex}`}>
                                        <StyledEvidenceMetadataHeader className='evidence-metadata-header'>
                                            <CustomStyledEvidenceMetadataHeader className={metadata.displayNameHtmlClasses}>
                                                {metadata.displayName}
                                            </CustomStyledEvidenceMetadataHeader>
                                        </StyledEvidenceMetadataHeader>
                                        { this.renderExternalEvidenceMetadataField(metadata, metadataIndex) }
                                    </div>
                                );
                            })
                        }
                    </div>
                </StyledEvidenceSectionBody>
            );
        }
        return null;
    }

    renderExternalEvidenceMetadataField(metadata, metadataIndex) {
        let valueType = EXTERNAL_EVIDENCE_DATA_TYPE.text;
        if (metadata.type === EXTERNAL_EVIDENCE_DATA_TYPE.link) {
            valueType = EXTERNAL_EVIDENCE_DATA_TYPE.link;
        } else if (metadata.type === EXTERNAL_EVIDENCE_DATA_TYPE.list) {
            if (metadata.listItemType === EXTERNAL_EVIDENCE_DATA_TYPE.link) {
                valueType = EXTERNAL_EVIDENCE_DATA_TYPE.link;
            }
        }
        let valueIndex = 0;
        return (
            <StyledEvidenceMetadata className='evidence-metadata-value'>
                {
                    metadata._valueArray.map((value) => {
                        valueIndex++;
                        let valueElement;
                        if (valueType === EXTERNAL_EVIDENCE_DATA_TYPE.link) {
                            valueElement = <a target="_blank" href={value} aria-label={metadata.displayName}>{value}</a>;
                        } else {
                            valueElement = value;
                        }
                        return (
                            <CustomStyledEvidenceMetadata className={metadata.valueHtmlClasses}
                               key={`metadata-${metadataIndex}-value-${valueIndex}`}
                               metadata={metadata} >
                                {valueElement}
                            </CustomStyledEvidenceMetadata>
                        );
                    })
                }
            </StyledEvidenceMetadata>
        );
    }

    render() {
        const { award, memberLogoUrl, memberWebsiteUrl,
            indianaAchievementShare, isOwner, learnerBannerUserImageUrl,
            dashboardUrl } = this.props;
        const { expired, showAchievementShareModal } = this.state;
        const showAchievementShareButton = indianaAchievementShare && isOwner
            && !award.archived && !expired;

        return (
            <StyledCredentialPage>
                <AchievementShareModal open={showAchievementShareModal}
                                       institutionId={award.issuer.id}
                                       learnerId={award.user.id}
                                       institutionName={award.issuer.name}
                                       credentialAwardId={award.id}
                                       credentialName={award.credential.name}
                                       emailAddress={award.user.email}
                                       onClose={() => this.setState({
                                           showAchievementShareModal: false
                                       })} />
                {
                    !award.archived && (
                        <LearnerInfoBanner
                            learnerBannerUserImageUrl=
                                {learnerBannerUserImageUrl}
                            learnerName={award.user.firstName + ' ' +
                                award.user.lastName}
                            dashboardUrl={dashboardUrl}
                            showAchievementShareButton=
                                {showAchievementShareButton}
                            achievementOnClick={() => {
                                this.setState({
                                    showAchievementShareModal: true
                                })
                            }} />
                    )
                }
                <Row className="credential-page-header">
                    <Col lg={6} md={6} sm={6}>
                        <MemberInfoBanner
                            member={award.issuer}
                            memberLogoUrl={memberLogoUrl}
                            organizationUrl={memberWebsiteUrl}
                        />
                    </Col>
                    {this.renderOrderButton()}
                </Row>

                <StyledCredentialSection className="credential-section">
                    <StyledCredentialSectionHeader className="section-header">
                        {award.credential.productName}
                    </StyledCredentialSectionHeader>

                    { this.renderCredentialSectionBanner() }

                    { this.renderCredentialSectionBody() }

                    { this.renderExternalEvidenceSectionBody() }
                </StyledCredentialSection>
            </StyledCredentialPage>
        )
    };
}

function mapStateToProps(state) {
    return {
        externalEvidence: state.externalEvidence,
        uiConfig: state.uiConfig
    }
}

function mapDispatchToProps(dispatch) {
    return bindActionCreators({
        getExternalEvidence,
        getUiConfig
    }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(App);
